import { useState } from "react"
import { IconCheckOutline18 as Check } from "nucleo-ui-outline-18"

import type { iconButtonVariantsType } from "@productlane/ui"
import type { Issue } from "@productlane/zero"
import { cn } from "@productlane/lib/src/cn"
import {
	IconButton,
	LinearIcon,
	Loader,
	Popover,
	Tooltip,
} from "@productlane/ui"
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"
import { mutators } from "@productlane/zero/mutators"

import {
	propertyIconClassName,
	PropertyTrigger,
} from "@/components/property-trigger"
import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { useRole } from "@/lib/useRole"
import { mutateOk, reportServerRejection, useZero } from "@/lib/zero"
import { IssuePriorityIcon, issuePriorityIcon } from "./issue-priority-icon"

interface Props {
	issue: Issue
	className?: string
	iconVariant?: iconButtonVariantsType
	withPriority?: boolean
}

export const IssuePrioritySelect = ({
	issue,
	className,
	iconVariant = "ghost-brightness",
	withPriority = false,
}: Props) => {
	const [isOpen, setIsOpen] = useState(false)
	const zero = useZero()
	const { role } = useRole()

	const { data: priorities } = api.linear.priorities.useQuery(undefined, {
		cacheTime: Infinity,
		staleTime: Infinity,
		enabled: isOpen,
	})

	const selectPriority = async (value: number, label: string) => {
		setIsOpen(false)
		if (label === issue.priority) {
			return
		}
		await mutateOk(
			reportServerRejection(
				zero.mutate(
					mutators.issues.update({
						id: issue.id,
						priority: { value, label },
					}),
				),
				(message) =>
					toast.error({
						title: `${label} was not saved in Linear`,
						description: message,
						icon: LinearIcon,
					}),
			),
		)
		analytics.track("issue-priority-update")
	}

	if (role === "VIEWER") {
		return withPriority ? (
			<PropertyTrigger
				className={className}
				icon={
					<IssuePriorityIcon
						priority={issue.priority}
						className={propertyIconClassName}
					/>
				}
			>
				{issue.priority}
			</PropertyTrigger>
		) : (
			<IssuePriorityIcon
				priority={issue.priority}
				className={propertyIconClassName}
			/>
		)
	}

	return (
		<Popover
			onOpenChange={setIsOpen}
			open={isOpen}
			className="p-0"
			content={
				!priorities ? (
					<div className="flex items-center justify-center py-8">
						<Loader />
					</div>
				) : (
					<Command loop>
						<CommandInput autoFocus placeholder="Change priority..." />
						<CommandList>
							<CommandEmpty>No matching priorities</CommandEmpty>
							<CommandGroup>
								{priorities.map(({ label, priority }) => (
									<CommandItem
										key={priority}
										value={label}
										keywords={[String(priority)]}
										onSelect={() => void selectPriority(priority, label)}
									>
										<div className="ml-0.5 flex w-full items-center gap-2 font-medium">
											<IssuePriorityIcon
												priority={label}
												className="h-3.5 w-3.5 flex-none text-secondary"
											/>
											<span className="flex-1 truncate">{label}</span>
											{issue.priority === label && (
												<Check className="h-3 w-3 flex-none" />
											)}
										</div>
									</CommandItem>
								))}
							</CommandGroup>
						</CommandList>
					</Command>
				)
			}
		>
			{withPriority ? (
				<PropertyTrigger
					tooltip="Change priority"
					className={className}
					icon={
						<IssuePriorityIcon
							priority={issue.priority}
							className={propertyIconClassName}
						/>
					}
				>
					{issue.priority}
				</PropertyTrigger>
			) : (
				<div className={cn("group", className)}>
					<Tooltip label="Change priority" shouldWrapChildren>
						<IconButton
							size="md"
							variant={iconVariant}
							aria-label={`Priority: ${issue.priority}`}
							Icon={issuePriorityIcon(issue.priority)}
							iconClassName="h-4 w-4"
							onClick={(e) => {
								e.preventDefault()
								setIsOpen(!isOpen)
							}}
						/>
					</Tooltip>
				</div>
			)}
		</Popover>
	)
}
